import { useState } from "react";

import { clsx } from "clsx";

import { RVIcon } from "../../atoms/RVIcon/RVIcon";
import { RVButton } from "../../molecules/RVButton/RVButton";
import { useButtonPosition } from "../../molecules/RVHelpButton/hooks/useButtonPosition";

import type { RVHelpButtonAnchorSide, RVHelpButtonAnchorVertical } from "../../molecules/RVHelpButton/RVHelpButton";

import styles from "./RVHelpPopup.module.scss";

const ANCHOR_SIDES = {
    LEFT: "left",
    RIGHT: "right",
} as const;

const ANCHOR_VERTICAL = {
    TOP: "top",
    BOTTOM: "bottom",
} as const;

const MAX_ATTEMPTS = 5;
const CSS_VAR_X = "--rv-help-popup-x";
const CSS_VAR_Y = "--rv-help-popup-y";

export type RVHelpPopupProps = {
    /** Name of the icon to display in the header. */
    icon: string;
    /** Text label shown in the header. Also used as the accessible button label. */
    label: string;
    /**
     * Content rendered inside the popup body when expanded.
     * Receives no special layout — fill it however you like.
     */
    children?: React.ReactNode;
    /** Which horizontal edge of the viewport the popup is anchored to. */
    anchorSide?: RVHelpButtonAnchorSide;
    /** Which vertical edge of the viewport the popup is anchored to. */
    anchorVertical?: RVHelpButtonAnchorVertical;
    /**
     * Additional className(s) — useful for setting initial position via CSS vars:
     * - `--rv-help-popup-x`: horizontal offset in px from `anchorSide`
     * - `--rv-help-popup-y`: vertical offset in px from `anchorVertical`
     */
    className?: string;
    /**
     * Width of the popup panel in px. Defaults to 320.
     * The header stretches to match this width when expanded.
     */
    width?: number;
    /**
     * Controlled expanded state. When provided, the popup is controlled and its
     * open/closed state is driven by this prop instead of internal state.
     */
    isExpanded?: boolean;
    /** Called with the next expanded state when the header is toggled. */
    onExpandedChange?: (expanded: boolean) => void;
};

/**
 * RVHelpPopup is a floating, draggable widget that expands in-place to reveal
 * a content panel when its header is clicked. At rest it looks identical to
 * `RVHelpButton` — an icon badge with a text label that slides in on hover.
 * Clicking toggles the expanded body open or closed.
 *
 * Use this when you need a floating launcher that shows richer content
 * (e.g. a quick-reference panel, an embedded FAQ, or a mini chat) without
 * navigating away from the page.
 *
 * Initial position can be provided via CSS variables on the wrapper element
 * (via `className`): `--rv-help-popup-x` and `--rv-help-popup-y`.
 *
 * `x` is interpreted as distance from the anchored horizontal edge (left/right)
 * and `y` is interpreted from the anchored vertical edge (top/bottom).
 *
 * @example
 * <RVHelpPopup icon="question-mark" label="Quick help">
 *     <p>Here is some helpful information.</p>
 * </RVHelpPopup>
 */
export function RVHelpPopup({
    icon,
    label,
    children,
    anchorSide = ANCHOR_SIDES.RIGHT,
    anchorVertical = ANCHOR_VERTICAL.TOP,
    className,
    width = 320,
    isExpanded: isExpandedProp,
    onExpandedChange,
}: RVHelpPopupProps): JSX.Element {
    const [internalExpanded, setInternalExpanded] = useState(false);
    const isControlled = isExpandedProp !== undefined;
    const isExpanded = isControlled ? isExpandedProp : internalExpanded;

    const {
        position,
        anchorSide: currentAnchorSide,
        anchorVertical: currentAnchorVertical,
        isDraggingState,
        wrapperRef,
        handleDragMouseDown,
    } = useButtonPosition({
        anchorSide,
        anchorVertical,
        className,
        cssVarX: CSS_VAR_X,
        cssVarY: CSS_VAR_Y,
        maxCssInitAttempts: MAX_ATTEMPTS,
    });

    const isRight = currentAnchorSide === ANCHOR_SIDES.RIGHT;
    const isBottom = currentAnchorVertical === ANCHOR_VERTICAL.BOTTOM;

    const wrapperStyle: React.CSSProperties = {
        ...(isBottom ? { bottom: position.y } : { top: position.y }),
        ...(isRight ? { right: position.x } : { left: position.x }),
        ...(isExpanded ? { width } : {}),
    };

    function handleHeaderClick() {
        if (!isDraggingState) {
            const next = !isExpanded;
            if (!isControlled) {
                setInternalExpanded(next);
            }
            onExpandedChange?.(next);
        }
    }

    return (
        <div
            ref={wrapperRef}
            className={clsx(
                styles.popupWrapper,
                isDraggingState && styles.isDragging,
                isExpanded && styles.isExpanded,
                isBottom && styles.isBottomAnchored,
                className
            )}
            style={wrapperStyle}
            data-testid="help-popup-wrapper"
        >
            {/* ── Header row ─────────────────────────────────────────── */}
            <div className={styles.header}>
                <RVButton
                    className={styles.dragHandle}
                    onMouseDown={handleDragMouseDown}
                    aria-label="Drag help popup"
                    data-testid="drag-handle"
                    icon="drag-handle"
                    iconSize="small"
                    type="text"
                    textVariant="icon"
                />

                <button
                    type="button"
                    className={styles.headerButton}
                    onClick={handleHeaderClick}
                    aria-expanded={isExpanded}
                    aria-label={label}
                    data-testid="header-button"
                >
                    <span className={styles.iconArea} data-testid="icon-area">
                        <RVIcon icon={icon} />
                    </span>

                    <span className={styles.labelArea} data-testid="label-area">
                        {label}
                    </span>

                    <span className={styles.chevronArea} aria-hidden="true">
                        <RVIcon
                            icon="arrow-down"
                            size="xsmall"
                            className={clsx(styles.chevronIcon, !isExpanded && styles.chevronRight)}
                        />
                    </span>
                </button>
            </div>

            {/* ── Expandable body ────────────────────────────────────── */}
            <div className={styles.body} aria-hidden={!isExpanded} data-testid="popup-body">
                <div className={styles.bodyInner}>{children}</div>
            </div>
        </div>
    );
}
